// © 2025 GridStorm / Tekivex — All Rights Reserved
// Unauthorized reproduction or distribution is prohibited.
/**
 * Notifiers — route a Finding to an outbound channel.
 *
 * Pluggable: implement `Notifier` to send findings anywhere (Slack SDK, email,
 * PagerDuty, your backend). The built-in `createWebhookNotifier` POSTs JSON
 * with retries + exponential backoff and an optional HMAC-SHA256 signature so
 * receivers can authenticate the sender. All notifiers are best-effort and
 * never throw.
 */

import type { Finding } from './types.js';

export interface NotifyResult {
  ok:       boolean;
  status?:  number;
  error?:   string;
  /** How many HTTP attempts were made (webhook notifier). */
  attempts?: number;
}

export interface Notifier {
  readonly name: string;
  notify(finding: Finding): Promise<NotifyResult>;
}

export interface WebhookNotifierOptions {
  /** Per-attempt timeout (ms). Default 8000. */
  timeoutMs?: number;
  /** Retries AFTER the first attempt, on network error / 5xx / 429. Default 3. */
  retries?: number;
  /** First backoff delay (ms); doubles per retry with small jitter. Default 1000. */
  backoffMs?: number;
  /**
   * Shared secret for HMAC-SHA256 signing. When set, each request carries
   * `x-dataflow-signature: sha256=<hex>` computed over the exact JSON body,
   * so the receiver can verify authenticity + integrity. Requires WebCrypto
   * (`globalThis.crypto.subtle` — Node >= 16 and all browsers); silently
   * skipped if unavailable.
   */
  hmacSecret?: string;
}

/**
 * POST findings to a webhook URL as JSON (Slack/Discord-friendly `text`).
 * Second parameter accepts a plain number for back-compat (timeoutMs).
 */
export function createWebhookNotifier(
  url: string,
  optsOrTimeout: number | WebhookNotifierOptions = {},
): Notifier {
  const opts: WebhookNotifierOptions =
    typeof optsOrTimeout === 'number' ? { timeoutMs: optsOrTimeout } : optsOrTimeout;
  const timeoutMs = opts.timeoutMs ?? 8000;
  const retries   = Math.max(0, opts.retries ?? 3);
  const backoffMs = Math.max(50, opts.backoffMs ?? 1000);

  return {
    name: 'webhook',
    async notify(finding) {
      if (!url.trim()) return { ok: false, error: 'no webhook url', attempts: 0 };

      const payload = {
        source: 'DataFlow Sentinel',
        severity: finding.severity,
        title: finding.title,
        text: `*[${finding.severity.toUpperCase()}] ${finding.title}*\n${finding.cause}\n→ ${finding.suggestedAction}`,
        column: finding.column,
        value: finding.value,
        confidence: finding.confidence,
        relatedColumn: finding.relatedColumn ?? null,
        evidence: finding.evidence,
        timestamp: finding.timestamp,
      };
      const body = JSON.stringify(payload);

      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (opts.hmacSecret) {
        const sig = await hmacSha256Hex(opts.hmacSecret, body);
        if (sig) headers['x-dataflow-signature'] = `sha256=${sig}`;
      }

      let attempts = 0;
      let last: NotifyResult = { ok: false, error: 'not attempted', attempts: 0 };

      for (let attempt = 0; attempt <= retries; attempt++) {
        attempts++;
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), timeoutMs);
        try {
          const res = await fetch(url, { method: 'POST', signal: ctrl.signal, headers, body });
          last = { ok: res.ok, status: res.status, attempts };
          // Success, or a non-retryable client error → stop.
          if (res.ok || (res.status < 500 && res.status !== 429)) return last;
        } catch (e) {
          last = { ok: false, error: e instanceof Error ? e.message : String(e), attempts };
        } finally {
          clearTimeout(timer);
        }
        if (attempt < retries) {
          const delay = backoffMs * Math.pow(2, attempt) * (1 + Math.random() * 0.25);
          await new Promise((r) => setTimeout(r, delay));
        }
      }
      return last;
    },
  };
}

/** Wrap any callback as a Notifier (e.g. push to your own queue). */
export function createCallbackNotifier(name: string, fn: (f: Finding) => void | Promise<void>): Notifier {
  return {
    name,
    async notify(finding) {
      try { await fn(finding); return { ok: true }; }
      catch (e) { return { ok: false, error: e instanceof Error ? e.message : String(e) }; }
    },
  };
}

// ─── HMAC-SHA256 via WebCrypto (isomorphic: browser + Node ≥ 16) ─────────────

async function hmacSha256Hex(secret: string, message: string): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  try {
    const enc = new TextEncoder();
    const key = await subtle.importKey(
      'raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'],
    );
    const sig = await subtle.sign('HMAC', key, enc.encode(message));
    return [...new Uint8Array(sig)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}
